import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { userSelector } from 'store/auth/selectors';
import { studentsSelector } from 'store/students/selectors';
import { getSchoolStudents } from 'store/students/actions';

import Students from './Students';

const PRESENCE_POLL_INTERVAL_MS = 30_000;

const StudentsContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { id: paramSchoolId } = useParams();

  const students = useAppSelector(studentsSelector);
  const user = useAppSelector(userSelector);

  const schoolId = paramSchoolId || user.schoolId;

  const [loading, setLoading] = useState(true);
  const [sortParams, setSortParams] = useState({
    sortBy: 'updatedAt',
    sortDirection: 'desc',
  });
  const sortParamsRef = useRef(sortParams);

  useEffect(() => {
    sortParamsRef.current = sortParams;
  }, [sortParams]);

  useEffect(() => {
    const _getStudentsBySchoolId = async () => {
      try {
        await dispatch(
          getSchoolStudents({
            schoolId,
            params: {
              page: 1,
              limit: 10,
              sortBy: sortParams.sortBy,
              sortDirection: sortParams.sortDirection,
            },
          }),
        ).unwrap();
      } catch (error) {
        console.warn(error);
      }

      setLoading(false);
    };

    _getStudentsBySchoolId();
  }, []);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const pollStudents = () => {
      void dispatch(
        getSchoolStudents({
          schoolId,
          params: {
            page: students.currentPage,
            limit: students.perPage,
            sortBy: sortParamsRef.current.sortBy,
            sortDirection: sortParamsRef.current.sortDirection,
          },
        }),
      );
    };

    const intervalId = window.setInterval(pollStudents, PRESENCE_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [dispatch, loading, schoolId, students.currentPage, students.perPage]);

  const handleSortChange = async (nextSortBy: any) => {
    try {
      await dispatch(
        getSchoolStudents({
          schoolId,
          params: {
            page: 1,
            limit: 50,
            sortBy: nextSortBy,
            sortDirection: sortParams.sortDirection === 'asc' ? 'desc' : 'asc',
          },
        }),
      ).unwrap();

      setSortParams((prevState: any) => ({
        sortBy: nextSortBy,
        sortDirection: prevState.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    } catch (error) {
      console.warn(error);
    }
  };

  const handleEditStudentClick = (student: any) => {
    navigate(`/students/${student.id}`);
  };

  const handleStudentRowClick = (studentId: string) => {
    navigate(`/students/${studentId}`);
  };

  return (
    <Students
      loading={loading}
      students={students.data}
      schoolId={schoolId}
      sortParams={sortParams}
      onSortChange={handleSortChange}
      onEditStudentClick={handleEditStudentClick}
      onStudentRowClick={handleStudentRowClick}
    />
  );
};

export default React.memo(StudentsContainer) as any;
